import * as React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import { addContext, addTranslation, translationConsumer } from '@estee/elc-service';
import { diContainer } from '../../../../service-setup/diContainer';
import serviceNames from '../../../../service-setup/ProductService';
import { ProductFiltersViewController } from '../../../controllers/ProductFiltersViewController';
import { classes } from '../../../utils/Classes';
import { FilterSet } from '../../../domain/entities/FilterSet';
import { observable, computed } from 'mobx';
import { IFiltersConfig } from '../../../interfaces/IFilters';
import { FilterOptionTypes, FilterSetTypes } from '../../../constants/Filters';
import { OrderRender } from '@estee/elc-layouts';
import { getFiltersViewRegistry } from '../../../utils/orderableUtils/getViewRegistry';
import {
    IFiltersFeatureData,
    IOrderConfigCollection
} from '../../../interfaces/IOrderableComponents';
import { ComponentTypes } from '~constants/ComponentTypes';
import { ArrayMinOne } from '@estee/elc-universal-utils';

export interface IFiltersTranslation {
    filtersLabel: string;
    clearFiltersLabel: string;
    applyFiltersLabel: string;
    filterByCategoryLabel: string;
    filterByPriceLabel: string;
    resultsLabel: string;
    filterByLabel: string;
    removeIconAriaLabel: string;
    collapseIconAriaLabel: string;
    expandIconAriaLabel: string;
}

export interface IOrderableFilters {
    filterSets: FilterSet[];
    appliedFilters: FilterSet[];
    productsNumber: number;
    totalAppliedFilters?: number;
    translations?: IFiltersTranslation;
    filtersConfig?: IFiltersConfig;
    scrollToGridTop?(): void;
}

const translationFields: ArrayMinOne<string> = [
    'filtersLabel',
    'applyFiltersLabel',
    'filterByLabel',
    'removeIconAriaLabel',
    'hideFiltersLabel'
];
@addContext()
@addTranslation(translationFields)
@translationConsumer(translationFields)
@observer
class OrderableFilters extends React.Component<IOrderableFilters> {
    @observable
    private productFiltersViewController: ProductFiltersViewController;
    private productFiltersOrder: IOrderConfigCollection;

    constructor(props: IOrderableFilters) {
        super(props);

        this.productFiltersViewController = diContainer.get(
            serviceNames.productFiltersViewController
        );
        this.productFiltersOrder = this.filtersOrderConfig as IOrderConfigCollection;
    }

    private get filtersOrderConfig() {
        const { filtersConfig } = this.props;

        return filtersConfig && filtersConfig.componentElementsOrderConfig;
    }

    private get filtersAsCheckboxes() {
        const filterOptionsType =
            this.props.filtersConfig &&
            this.props.filtersConfig.filterOptions &&
            this.props.filtersConfig.filterOptions.type;

        return filterOptionsType === FilterOptionTypes.CHECKBOX;
    }

    @computed
    private get activeFilterSet() {
        const allowAllFiltersCollapsed =
            this.props.filtersConfig && this.props.filtersConfig.allowAllFiltersCollapsed;
        const type = this.props.filtersConfig && this.props.filtersConfig.filterSets.type;
        let activeFilter = this.props.filterSets.filter((_filterSet) => _filterSet.isOpen)[0];
        if (!allowAllFiltersCollapsed && type !== FilterSetTypes.ACCORDIONS && !activeFilter) {
            activeFilter = this.props.filterSets[0];
        }

        return activeFilter;
    }

    @computed
    private get isFiltersOverlayOpen() {
        return this.productFiltersViewController.data.isFiltersModalOpen;
    }

    private handleFilterSetToggle = (filterSet: FilterSet) => {
        const allowAllFiltersCollapsed =
            this.props.filtersConfig && this.props.filtersConfig.allowAllFiltersCollapsed;
        const filterSetsConfig = this.props.filtersConfig && this.props.filtersConfig.filterSets;
        const type = filterSetsConfig && filterSetsConfig.type;
        const activeFilterSets = this.props.filterSets.filter((_filterSet) => _filterSet.isOpen);

        if (type === FilterSetTypes.TOGGLES) {
            const activeFilterClicked =
                activeFilterSets[0] && activeFilterSets[0].key === filterSet.key;

            if (activeFilterClicked && !allowAllFiltersCollapsed) {
                return null;
            }

            filterSet.toggleOpenState();
            // close all colapsed filters
            activeFilterSets[0] && activeFilterSets[0].setOpenState(false);
        } else if (type === FilterSetTypes.ACCORDIONS) {
            filterSet.toggleOpenState();
        }
    };

    private toggleFilters = () => {
        if (this.productFiltersViewController.data.isFiltersModalOpen) {
            this.productFiltersViewController.onCloseFiltersClick();
        } else {
            this.productFiltersViewController.onOpenFiltersClick();
        }
    };

    private closeOverlayHandler = () => {
        this.productFiltersViewController.onCloseFiltersClick();
        this.props.scrollToGridTop && setTimeout(this.props.scrollToGridTop, 0);
    };

    public render() {
        const filtersConfig = this.props.filtersConfig as IFiltersConfig;
        const orderableFiltersWrapperClassNames = classnames(classes('orderable-filters-wrapper'), {
            'filters-applied': !!this.props.appliedFilters.length
        });
        const filtersModalClassnames = classnames(classes('mobile-filters-modal'), {
            'is-open': this.productFiltersViewController.data.isFiltersModalOpen
        });

        const featureConfig: IFiltersFeatureData = {
            ...this.productFiltersViewController.data,
            onClearAllAppliedFilters: this.productFiltersViewController.onClearAllAppliedFilters,
            onOpenFiltersClick: this.productFiltersViewController.onOpenFiltersClick,
            onCloseFiltersClick: this.productFiltersViewController.onCloseFiltersClick,
            appliedFilters: this.props.appliedFilters,
            toggleFilters: this.toggleFilters,
            filterSets: this.props.filterSets,
            totalAppliedFilters: this.props.totalAppliedFilters,
            activeFilterSet: this.activeFilterSet,
            filtersAsCheckboxes: this.filtersAsCheckboxes,
            filterSetConfig: filtersConfig.filterSets,
            handleFilterSetToggle: this.handleFilterSetToggle,
            itemsNumber: this.props.productsNumber,
            showIndividualFilterByLabel: filtersConfig.showIndividualFilterByLabel,
            selectedFilterValues: filtersConfig.selectedFilterValues,
            isOverlayOpen: this.isFiltersOverlayOpen,
            translations: this.props.translations,
            closeOverlay: this.closeOverlayHandler,
            closeOnOverlayMaskClick: filtersConfig.closeOnOverlayMaskClick,
            scrollToGridTop: this.props.scrollToGridTop,
            overlayClassNames: filtersModalClassnames,
            componentType: ComponentTypes.PRODUCT_FILTERS
        };

        return (
            <div className={orderableFiltersWrapperClassNames}>
                {Object.keys(this.productFiltersOrder).map((sectionName: string) => (
                    <div className={classes(`${sectionName}-section`)} key={sectionName}>
                        <OrderRender
                            data-test-id={`${sectionName}-section`}
                            orderConfig={this.productFiltersOrder[sectionName]}
                            serviceName={'elc-service-prodcat'}
                            viewRegistry={getFiltersViewRegistry<IFiltersFeatureData>(
                                featureConfig
                            )}
                        />
                    </div>
                ))}
            </div>
        );
    }
}

export { OrderableFilters };
